import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import FlexBox from 'flexbox-react';
import { ThemeProvider } from 'react-css-themr';
import { CardActions, Switch, Tabs, Tab } from 'react-toolbox';
import Card from 'components/Card';
import ColoredButton from 'components/ColoredButton';
import UploadsTable from 'components/UploadsTable';
import UploadDialog from 'components/UploadDialog';
import Unauthorized from 'components/Unauthorized';
import ConflictingAppointmentModal from 'conflictingAppointmentModal';
import styles from 'styles/form';
import RTCard from 'styles/card';
import RTInput from 'styles/input';
import RTSwitch from 'styles/switch';
import RTTabs from 'styles/tabs';
import { EMAIL_VALIDATION_REGEX } from 'config/constants';
import { can } from '../../../../../services';
import getAllowedInstallerTabs from '../installerAllowedTabs';
import { filterTypes, getRandomStr } from '../../../../../utils';
import { isEqual, uniqueId, isString, cloneDeep, uniq } from 'lodash';
import { handleDateSave } from '../../../../../utils/dateUtils';
import InstallerBasicInfoForm from './installerBasicInfoForm';
import InstallerCertificationsForm from './installerCertificationsForm';
import InstallerCrewForm from './installerCrewForm';
import { RoleType } from 'types/user';
import WorkroomType from 'types/workroom';
import { requestEmployee } from 'actionCreators';
import { getEmployeeById } from '../../../../../reducers';
import { permissionByWorkroomId } from '../../../../../utils/permissionUtils';

const theme = { RTInput, RTCard, RTSwitch, RTTabs };
const cancelClass = cx([styles.cancelButton, styles.actionButton]);
const isNew = obj => isString(obj.id) && obj.id.indexOf('new_') === 0;
const AddressDefault = {
  street: '',
  city: '',
  state: '',
  zip_code: '',
};
const CrewSpecificDefault = {
  hardwood_sf_per_day: 0,
  carpet_sf_per_day: 0,
  tile_sf_per_day: 0,
  laminate_sf_per_day: 0,
  vinyl_sf_per_day: 0,
  vinyl_plank_sf_per_day: 0,
  backsplash_sf_per_day: 0,
  can_speak_english: false,
  can_speak_spanish: false,
  can_move_furniture: false,
  can_move_furniture_oversized: false,
  can_move_appliances: false,
  can_do_subfloor_prep: false,
  can_do_subfloor_replacement: false,
  can_do_naildown_hardwood: false,
  can_do_gluedown_hardwood: false,
  can_do_floating_hardwood: false,
  can_do_cali_bamboo_hardwood: false,
  can_do_steps_hardwood: false,
  can_do_stretch_in_carpet: false,
  can_do_gluedown_carpet: false,
  can_do_binding_carpet: false,
  can_do_pattern_match_carpet: false,
  can_do_berber_carpet: false,
  can_do_steps_carpet: false,
  can_do_floor_tile: false,
  can_do_backsplash_tile: false,
  can_do_backsplash_glass: false,
  can_do_backsplash_blocking: false,
  can_do_patterns_tile: false,
  can_do_large_format_tile: false,
  can_do_vinyl_sheet: false,
  can_do_vct_lvt: false,
  can_do_gluedown_vinyl_plank: false,
  can_do_floating_vinyl_plank: false,
  can_remove_replace_toilet: false,
  can_remove_naildown_hardwood: false,
  can_remove_floating_hardwood: false,
  can_remove_gluedown_hardwood: false,
  can_remove_over_pad_carpet: false,
  can_remove_gluedown_carpet: false,
  can_remove_laminate: false,
  can_remove_tile: false,
  can_remove_gluedown_vinyl: false,
  can_remove_vinyl_plank: false,
  same_day_multi_surface: false,
  max_travel_time: 0,
  phone_call_needed: false,
};
const crewValidations = [
  'name', 'email', 'badge_number', 'poc_name', 'badge_expiration',
  'phone_number', 'workrooms', 'surface_types', 'covered_zip_codes',
];
const crewSpecificValidations = ['max_travel_time'];
const installerValidations = [
  'name', 'email', 'primary_phone',
  'timezone', 'visibility', 'address',
];
const helperValidations = ['email'];

class InstallerForm extends Component {
  constructor(props) {
    super(props);
    this.handleSave = this.handleSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleActive = this.handleChange.bind(this, 'active');
    this.handlePrimaryType = this.handleChange.bind(this, 'primary_phone_type');
    this.handleSecondaryType = this.handleChange.bind(this, 'secondary_phone_type');
    this.handleState = this.handleChange.bind(this, 'state');
    this.removeCrew = this.removeCrew.bind(this);
    this.removeHelper = this.removeHelper.bind(this);
    this.handleHelperChange = this.handleHelperChange.bind(this);
    this.handleCrewChange = this.handleCrewChange.bind(this);
    this.handleCrewSpecificChange = this.handleCrewSpecificChange.bind(this);
    this.handleAvailabilityChange = this.handleAvailabilityChange.bind(this);
    this.handleDailyChange = this.handleDailyChange.bind(this);
    this.addHelper = this.addHelper.bind(this);
    this.addCrew = this.addCrew.bind(this);
    this.addSurfaceTypeToCrew = this.addSurfaceTypeToCrew.bind(this);
    this.handleDisableChange = this.handleDisableChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleDialogSubmit = formData => this.props.onPayRateUpload(formData);
    this.fieldError = this.fieldError.bind(this);
    const { installer, helpers, crews } = this.props;
    this.state = {
      showUploadDialog: false,
      showApptWarningDialog: false,
      workroomChange: false,
      installer: (installer && { ...installer }) || {
        id: '',
        user_id: null,
        active: true,
        primary_phone_type: 'mobile',
        secondary_phone_type: 'mobile',
        address: AddressDefault,
      },
      helpers: helpers || [],
      crews: crews || [{
        id: uniqueId('new_'),
        active: false,
        name: 'Crew 1',
        phone_number_type: 'mobile',
        surface_types: [],
        default_start_time: '08:00',
        workrooms: [],
        covered_zip_codes: [],
        crew_specific: CrewSpecificDefault,
      }],
      allowedTabs: getAllowedInstallerTabs(
        props.role,
        (props.user && props.user.manage_vendor_workrooms) || {},
      ),
      errors: this.props.errors || {},
    };
  }

  componentWillMount = () => this.props.onTabChange(0);

  componentDidMount = () => this.props.requestEmployee(this.props.user.employable_id);

  componentWillReceiveProps = (nextProps) => {
    if (nextProps.errors && Object.keys(nextProps.errors) &&
      !isEqual(this.props.errors, nextProps.errors)) {
      this.goToFirstErroredTab(nextProps);
      this.setState({ errors: nextProps.errors });
    }
  }

  handleChange = (key, value) => {
    const installer = this.state.installer;
    if ({}.hasOwnProperty.call(AddressDefault, key)) {
      const currentAddress = installer.address || AddressDefault;
      installer.address = { ...currentAddress, [key]: value };
    } else installer[key] = key === 'email' ? value.replaceAll(' ', '') : value;
    this.setState({ installer });
    const isEdit = !!installer.id;
    const crews = this.state.crews;
    if (!isEdit && crews.length === 1 &&
      ['name', 'primary_phone_type', 'primary_phone'].indexOf(key) > -1) {
      const attrMap = {
        name: 'poc_name',
        primary_phone_type: 'phone_number_type',
        primary_phone: 'phone_number',
      };
      crews[0][attrMap[key]] = value;
      this.setState({ crews });
    }
    if (key === 'active' && !value) {
      for (let i = 0; i < crews.length; i += 1) {
        crews[i].active = false;
      }
      this.setState({ crews });
    }
  }

  handleCancel = () => this.props.onCancel();

  handleSave = () => {
    const { installer, helpers, crews } = this.state;
    const { address, user_id, name, email, primary_phone,
      primary_phone_type, secondary_phone, secondary_phone_type,
      timezone, visibility, active, ...rest } = installer;
    const data = {
      ...rest,
      workers_comp_expiration: handleDateSave(installer.workers_comp_expiration),
      liability_insurance_expiration: handleDateSave(installer.liability_insurance_expiration),
      installer_firm_certification_expiration:
        handleDateSave(installer.installer_firm_certification_expiration),
      assigned_renovator_certification_expiration:
        handleDateSave(installer.assigned_renovator_certification_expiration),
      visibility,
      user_attributes: {
        id: user_id,
        name,
        email,
        primary_phone,
        secondary_phone,
        primary_phone_type,
        secondary_phone_type,
        timezone,
        active,
      },
      address_attributes: address,
      helpers_attributes: helpers.map(helper => ({
        ...helper,
        id: isNew(helper) ? undefined : helper.id,
        badge_expiration: handleDateSave(helper.badge_expiration),
      })),
      crews_attributes: crews.map((crew) => {
        const dailyAvailability = crew.daily_availability ? crew.daily_availability :
          { morning: [2, 3, 4, 5, 6], afternoon: [2, 3, 4, 5, 6] };
        return {
          ...crew,
          id: isNew(crew) ? undefined : crew.id,
          badge_expiration: handleDateSave(crew.badge_expiration),
          surface_type_ids: crew.surface_types,
          workroom_ids: crew.workrooms.map(w => w.value || w),
          // temporarily auto filling if not dedicated
          daily_availability: {
            morning: dailyAvailability.morning,
            afternoon: (crew.delivery_dedicated || false) ?
              dailyAvailability.afternoon :
              dailyAvailability.morning,
          },
          crew_specific_attributes: crew.crew_specific,
        };
      }),
    };
    this.props.onSave(data)
      .then(() => this.props.onTabChange(0))
      .then(() => window.location.reload())
      .catch(() => {}); // validation failed: keep redux errors, no reload
  }

  handleApptWarningModal = display => this.setState({ showApptWarningDialog: display });

  emailsTheSame = (email1 = '', email2 = '') => email1.toLowerCase() === email2.toLowerCase();

  addHelper = () => this.setState({ helpers: this.state.helpers.concat({ phone_number_type: 'mobile', id: uniqueId('new_') }) });

  addCrew = () => {
    const crews = this.state.crews;
    this.setState({
      crews: crews.concat({
        id: uniqueId('new_'),
        active: false,
        phone_number_type: 'mobile',
        name: 'Crew '.concat((crews.length + 1)),
        surface_types: [],
        workrooms: [],
        email: `${getRandomStr()}@ChangeMe.com`,
        covered_zip_codes: [],
        store_zips_dictionary: { 'Additional Zip Codes': [] },
        crew_specific: CrewSpecificDefault,
        install_availability: [],
        delivery_availability: [],
        appointments: [],
        daily_availability: { morning: [], afternoon: [] },
      }),
    });
  }

  removeCrew = id => this.setState({
    crews: this.state.crews.filter(h => h.id !== id),
  });

  removeHelper = id => this.setState({
    helpers: this.state.helpers.filter(h => h.id !== id),
  });

  handleHelperChange = (id, key, value) => {
    const helpers = this.state.helpers;
    const helper = helpers.find(h => h.id === id);
    if (helper) helper[key] = key === 'email' ? value.replaceAll(' ', '') : value;
    this.setState({ helpers });
  }

  handleCrewChange = (id, key, value) => {
    const { updateCrewChanges, installerFormType } = this.props;
    const isEdit = installerFormType === 'Edit';
    const crews = cloneDeep(this.state.crews);
    const crew = crews.find(c => c.id === id);
    if (!crew) return;
    if (key === 'covered_zip_codes') {
      crew[key] = value.zipsFromStoreZipsDictArr;
      crew.store_zips_dictionary = value.newStoreZipsDict;
      this.setState({ crews, workroomChange: false });
    } else if (key === 'workrooms') {
      const wrkmVal = value.map(wrkm => wrkm.value);
      crew[key] = wrkmVal;
      // Only adds history event if editing existing profile, not on creation (new)
      if (installerFormType === 'Edit') updateCrewChanges('workrooms', wrkmVal, crew.id);
      this.setState({ crews, workroomChange: true });
    } else {
      if (key === 'can_deliver' && !value) {
        crew.delivery_dedicated = false;
        if (isEdit) updateCrewChanges('delivery_dedicated', false, crew.id);
      }
      if (key === 'delivery_dedicated' && value) {
        crew.can_deliver = true;
        if (isEdit) updateCrewChanges('can_deliver', true, crew.id);
      }
      crew[key] = key === 'email' ? value.replaceAll(' ', '') : value;
      if (isEdit) updateCrewChanges(key, value, crew.id);
      this.setState({ crews });
    }
  }

  handleCrewSpecificChange = (id, key, value) => {
    const crews = this.state.crews;
    const crew = crews.find(c => c.id === id);
    if (!crew) return;
    if (key === 'can_move_furniture_oversized' && value) crew.crew_specific.can_move_furniture = true;
    crew.crew_specific[key] = value;
    this.setState({ crews });
  }

  handleDisableChange = (id) => {
    const helpers = this.state.helpers;
    const helper = helpers.find(h => h.id === id);
    if (helper) helper.disabled = !helper.disabled;
    this.setState({ helpers });
  }

  addSurfaceTypeToCrew = (id, surfaceTypeId, checked) => {
    const { updateCrewChanges, installerFormType } = this.props;
    const crews = this.state.crews;
    const crew = crews.find(c => c.id === id);
    const surfaceTypes = crew.surface_types || [];
    const index = surfaceTypes.indexOf(surfaceTypeId);
    if (!checked && index > -1) surfaceTypes.splice(index, 1);
    else if (checked && !(index > -1)) surfaceTypes.push(surfaceTypeId);
    if (installerFormType === 'Edit') updateCrewChanges('surface_types', surfaceTypes, crew.id);
    this.setState({ crews });
  }

  handleAvailabilityChange = (id, val) => {
    const { updateCrewChanges, installerFormType } = this.props;
    const crews = this.state.crews;
    const crew = crews.find(c => c.id === id);
    const [dayString, type] = val.split('_');
    const day = parseInt(dayString, 10);
    if (type === 'off') {
      crew.install_availability = crew.install_availability.filter(d => d !== day);
      crew.delivery_availability = crew.delivery_availability.filter(d => d !== day);
    } else {
      crew[`${type}_availability`].push(day);
      if (type === 'install' && crew.delivery_availability.includes(day)) {
        const index = crew.delivery_availability.indexOf(day);
        crew.delivery_availability.splice(index, 1);
      } else if (type === 'delivery' && crew.install_availability.includes(day)) {
        const index = crew.install_availability.indexOf(day);
        crew.install_availability.splice(index, 1);
      }
    }
    crew.install_availability = uniq(crew.install_availability.sort());
    crew.delivery_availability = uniq(crew.delivery_availability.sort());
    if (installerFormType === 'Edit') {
      updateCrewChanges('install_availability', crew.install_availability, crew.id);
      updateCrewChanges('delivery_availability', crew.delivery_availability, crew.id);
    }
    this.setState({ crews });
  }

  handleDailyChange = (id, label, val) => {
    const { updateCrewChanges, installerFormType } = this.props;
    const crews = this.state.crews;
    const crew = crews.find(c => c.id === id);
    if (crew.delivery_dedicated) crew.daily_availability[label] = val;
    else crew.daily_availability = { morning: val, afternoon: val };
    // hiding logic related to appt type availability for now
    // crew.daily_availability = this.filterDailyAvailability(
    //   crew.install_availability, crew.delivery_availability, crew.daily_availability,
    // );
    // adding logic to keep appt. type availability somewhat up to date for now
    crew.install_availability = crew.delivery_dedicated ? [] : val;
    crew.delivery_availability = crew.delivery_dedicated ? val : [];
    if (installerFormType === 'Edit') {
      updateCrewChanges('daily_availability', crew.daily_availability, crew.id);
    }
    this.setState({ crews });
  }

  filterDailyAvailability = (dedicated, installAvail, deliveryAvail, { morning, afternoon }) =>
  (dedicated ? ({
    morning: this.filterTimeWindow(morning, installAvail, deliveryAvail),
    afternoon: this.filterTimeWindow(afternoon, installAvail, deliveryAvail),
  }) : ({
    morning: this.filterTimeWindow(morning, installAvail, deliveryAvail),
    afternoon: this.filterTimeWindow(afternoon, installAvail, deliveryAvail),
  }));

  filterTimeWindow = (window, installAvail, deliveryAvail) =>
    window.filter(d => installAvail.concat(deliveryAvail).includes(d));

  goToFirstErroredTab = (state = this.state) => {
    const { errors, activeTab } = state;
    if (!Object.keys(errors).length) return;
    const tabErrors = this.tabErrors(state);
    // if there are errors on this tab, do nothing
    if (tabErrors[this.state.allowedTabs[activeTab].label]) return;
    const firstErroredTab = this.state.allowedTabs.map(t => t.label).indexOf(
      Object.keys(tabErrors).filter(t => tabErrors[t] > 0)[0],
    );
    this.props.onTabChange(Math.max(firstErroredTab, 0));
  }

  tabErrors = (state = this.state) => {
    const { errors } = state;
    if (!Object.keys(errors).length) return {};
    const helperErrors = filterTypes('helpers', errors);
    const crewErrors = filterTypes('crews', errors);
    const crewTabErrors = Object.assign(helperErrors, crewErrors);
    // Basic Info errors are all those that are not crew tab errors
    const basicInfoErrors = {};
    Object.keys(errors).forEach((e) => {
      if (!crewTabErrors[e]) basicInfoErrors[e] = errors[e];
    });
    const certKeys = [
      'installer_firm_certification_number',
      'installer_firm_certification_expiration',
      'assigned_renovator_certification_number',
      'assigned_renovator_certification_expiration',
    ];
    const certErrorCount = certKeys.filter(k => basicInfoErrors[k]).length;
    const basicInfoWithoutCerts = { ...basicInfoErrors };
    certKeys.forEach((k) => { delete basicInfoWithoutCerts[k]; });
    const hasMillworkCertTab = this.state.allowedTabs
      .some(tab => tab.label === 'Millwork Certifications');
    const basicInfoErrorCount = Object.keys(basicInfoWithoutCerts).length +
      (hasMillworkCertTab ? 0 : certErrorCount);

    return {
      'Basic Info': basicInfoErrorCount,
      'Millwork Certifications': hasMillworkCertTab ? certErrorCount : 0,
      Crew: Object.keys(crewTabErrors).length,
    };
  }

  validateEmailInput = (emailToCheck, crewOrHelper = null) => {
    const forWho = `for ${crewOrHelper ? crewOrHelper.name : 'installer'}`;
    if (!emailToCheck) return !crewOrHelper || crewOrHelper.isCrew ? `Input email ${forWho}` : '';
    const regex = new RegExp(EMAIL_VALIDATION_REGEX);
    if (!regex.test(emailToCheck)) return `Input valid email ${forWho}`;
    const { installer: { email = null } } = this.state;
    let emailArr = [];
    if (crewOrHelper && email) emailArr.push(email);
    emailArr = this.addEmailsFromCrewOrHelper(emailArr, crewOrHelper);
    if (emailArr.length && emailArr.some(e => e.toLowerCase() === emailToCheck.toLowerCase())) {
      return `Input unique email ${forWho}`;
    }
    return '';
  }

  addEmailsFromCrewOrHelper = (emailArr, crewOrHelper) => {
    const { crews = [], helpers = [] } = this.state;
    let newEmailArr = emailArr || [];
    newEmailArr = this.addEmailsToArr(newEmailArr, crews, crewOrHelper);
    newEmailArr = this.addEmailsToArr(newEmailArr, helpers, crewOrHelper, false);
    return newEmailArr;
  }

  addEmailsToArr = (emailArr, list, crewOrHelper, isCrewList = true) => {
    if (!list.length) return emailArr;
    const newEmailArr = emailArr || [];
    list.forEach((l) => {
      const { id = null, email = null } = l;
      if ((!crewOrHelper ||
           id !== crewOrHelper.id ||
           (isCrewList && !crewOrHelper.isCrew) ||
           (!isCrewList && crewOrHelper.isCrew)) && email) newEmailArr.push(email);
    });
    return newEmailArr;
  }

  validateZip = (zip) => {
    if (!zip) return 'Input zip code';
    const regex = new RegExp(/(^\d{5}$)|(^\d{5}-\d{4}$)/);
    return regex.test(zip) ? '' : 'Input valid zip code';
  }

  validateAddress = (address) => {
    const safeAddress = address || AddressDefault;
    const addressKeys = Object.keys(safeAddress).filter(k => k !== 'id');
    const addressArr = [];
    addressKeys.forEach((k) => {
      const msgObj = { field: k, msg: '' };
      const fieldVal = safeAddress[k];
      if (k === 'zip_code') {
        const zipMsg = this.validateZip(fieldVal);
        if (zipMsg) msgObj.msg = zipMsg;
      } else if (!fieldVal) msgObj.msg = `Input ${k}`;
      if (msgObj.msg) addressArr.push(msgObj);
    });
    return addressArr;
  }

  installerSubmitDisabledMsgs = () => {
    const { installer, crews } = this.state;
    let msgArr = [];
    if (!crews.length) msgArr.push({ field: 'crews', msg: 'Add a crew' });
    installerValidations.forEach((v) => {
      const msgObj = { field: v, msg: '' };
      const fieldVal = installer[v] || null;
      if (v === 'email') {
        const emailMsg = this.validateEmailInput(fieldVal);
        if (emailMsg) msgObj.msg = emailMsg;
      } else if (v === 'address') {
        const addressArr = this.validateAddress(fieldVal);
        if (addressArr.length) msgArr = msgArr.concat(addressArr);
      } else if (!fieldVal) msgObj.msg = `Input ${v.replace('_', ' ')}`;
      if (msgObj.msg) msgArr.push(msgObj);
    });
    return msgArr;
  }

  crewHelperSubmitDisabledMsgs = (isCrew = true) => {
    const { crews = [], helpers = [] } = this.state;
    const contractorArr = isCrew ? crews : helpers;
    const validations = isCrew ? crewValidations : helperValidations;
    const msgArr = [];
    if (contractorArr.length) {
      contractorArr.forEach((c) => {
        validations.forEach((v) => {
          const msgObj = { id: c.id, field: v, msg: '' };
          const fieldVal = c[v] || null;
          if (v === 'email') {
            const emailMsg = this.validateEmailInput(fieldVal, { ...c, isCrew });
            if (emailMsg) msgObj.msg = emailMsg;
          } else if (!fieldVal || (Array.isArray(fieldVal) && !fieldVal.length)) {
            msgObj.msg = `Input ${c.name || `${isCrew ? 'crew' : 'helper'}`} ${v.replace('_', ' ')}`;
          }
          if (msgObj.msg) msgArr.push(msgObj);
        });
        if (isCrew) {
          crewSpecificValidations.forEach((v) => {
            const fieldVal = c.crew_specific ? c.crew_specific[v] : null;
            if (fieldVal === null || fieldVal === undefined || fieldVal === '') {
              msgArr.push({
                id: c.id,
                field: v,
                msg: `Please enter a value for ${v.replaceAll('_', ' ')}`,
              });
            }
          });
        }
      });
    }
    return msgArr;
  }

  // helperSubmitDisabledMsgs = () => {
  //   const { helpers } = this.state;
  //   const msgArr = [];
  //   if (helpers.length) {
  //     helpers.forEach((h) => {

  fieldError = (field, warnings) => {
    const fieldError = warnings.find(w => w.field === field) || null;
    return fieldError ? fieldError.msg : null;
  }

  crewsAppts = () => {
    const { crews = [] } = this.state;
    if (!crews.length || !crews.some(c => c.appointments && c.appointments.length)) return [];
    return crews.map(c => c.appointments).reduce((a, b) => a.concat(b), []);
  }

  renderTabContent = (isEdit, {
    invalidInstallerFieldsMsgs, invalidCrewFieldsMsgs, invalidHelperFieldsMsgs,
  }) => {
    const { installer, helpers, crews, workroomChange, errors } = this.state;
    const { activeTab, allWorkrooms, payRateUploadsData = [],
            role, handleCrewPayId, crewPayId } = this.props;
    switch ((this.state.allowedTabs[activeTab] || {}).label || '') {
      case 'Basic Info':
        return (<InstallerBasicInfoForm
          installer={installer}
          crews={crews}
          errors={errors}
          invalidInstallerFieldsMsgs={invalidInstallerFieldsMsgs}
          theme={theme}
          onChange={this.handleChange}
          role={role}
          position={(this.props.user && this.props.user.position) || ''}
          mvw={(this.props.user && this.props.user.manage_vendor_workrooms) || {}}
          allWorkrooms={allWorkrooms}
          fieldError={this.fieldError}
        />);
      case 'Millwork Certifications':
        return (<InstallerCertificationsForm
          installer={installer}
          onChange={this.handleChange}
          role={role}
          errors={errors}
        />);
      case 'Pay Rates':
        return isEdit ? (<div className={styles.divider}>
          <UploadDialog
            active={this.props.showUploadDialog}
            title="Upload Pay Rates"
            formDataKey="labor_rate_upload[asset]"
            handleToggle={this.props.onUploadDialogToggle}
            handleSubmit={this.handleDialogSubmit}
            handleCrewPayId={handleCrewPayId}
            crewPayId={crewPayId}
            errors={this.props.uploadError}
            setErrorMessage={this.props.setUploadErrorMessage}
            fileLoading={this.props.fileLoading}
            crews={crews.length ? crews : null}
          />
          <UploadsTable
            role={role}
            isPayRate
            downloadColumnName="Download"
            uploadText="Upload pay rates"
            data={payRateUploadsData}
            handleUpload={this.props.onUploadDialogToggle}
            crews={crews.length ? crews : null}
          />
        </div>) : null;
      case 'Crew':
        return (<InstallerCrewForm
          helpers={helpers}
          crews={crews}
          errors={errors}
          invalidCrewFieldsMsgs={invalidCrewFieldsMsgs || []}
          invalidHelperFieldsMsgs={invalidHelperFieldsMsgs || []}
          theme={theme}
          onHelperChange={this.handleHelperChange}
          onCrewChange={this.handleCrewChange}
          onCrewSpecificChange={this.handleCrewSpecificChange}
          onAddHelper={this.addHelper}
          onAddCrew={this.addCrew}
          onSurfaceTypesChange={this.addSurfaceTypeToCrew}
          onAvailabilityChange={this.handleAvailabilityChange}
          onDailyChange={this.handleDailyChange}
          onRemoveCrew={this.removeCrew}
          onDeleteHelper={this.removeHelper}
          onDisableChange={this.handleDisableChange}
          allWorkrooms={allWorkrooms}
          workroomChange={workroomChange}
          role={role}
          fieldError={this.fieldError}
        />);
      default:
        return null;
    }
  }

  renderTitle = (installer, isEdit) => {
    const title = isEdit ? (`${installer.name} (${installer.id})` || 'Edit Installer') : 'Add Installer';
    const switchClassNames = cx({
      [styles.switchTitleAdd]: !isEdit,
      [styles.switchTitle]: true,
    });
    const tabErrors = this.tabErrors();
    const canEdit = can(this.props.role, 'update', 'installer');
    return (<div>
      <FlexBox justifyContent="space-between" className={styles.headerDivider}>
        <FlexBox className={styles.titleTabs}>
          <div className={styles.headerChildren}>
            <h5 className={styles.cardTitle}>{title}</h5>
            <p className={styles.cardSubtitle}>
              Please ensure that all fields contain accurate information
            </p>
          </div>
          <div className={theme.tabBar}>
            <Tabs
              index={this.props.activeTab}
              onChange={this.props.onTabChange}
              className={RTTabs.headerOnlyTabs}
            >
              {this.state.allowedTabs.filter(tab => tab.editable).map(tab => (<Tab
                key={tab.label}
                label={tab.label}
                icon={tabErrors[tab.label] ? 'error' : ''}
                className={
                  `${(isEdit || (tab.label !== 'Pay Rates' && tab.label !== 'Time Off')) ? '' : styles.doNotDisplay
                  } ${
                  tabErrors[tab.label] ? RTTabs.inError : ''}`
                }
              />))}
            </Tabs>
          </div>
        </FlexBox>
        <div className={styles.headerChildren}>
          <Switch
            name="active"
            label="Account Active"
            onChange={this.crewsAppts().length ?
              () => this.handleApptWarningModal(true) : this.handleActive}
            className={switchClassNames}
            checked={installer.active}
            disabled={!canEdit}
          />
        </div>
      </FlexBox>
    </div>);
  }

  render = () => {
    const { installer, showApptWarningDialog } = this.state;
    const { role, workrooms, employee = null } = this.props;
    const isEdit = !!installer.id;
    const invalidInstallerFieldsMsgs = this.installerSubmitDisabledMsgs();
    const invalidCrewFieldsMsgs = this.crewHelperSubmitDisabledMsgs();
    const invalidHelperFieldsMsgs = this.crewHelperSubmitDisabledMsgs(false);
    const fieldsMsgs = invalidInstallerFieldsMsgs.map(m => m.msg)
      .concat(invalidCrewFieldsMsgs.map(m => m.msg))
      .concat(invalidHelperFieldsMsgs.map(m => m.msg));
    const { errors, crews } = this.state;
    const serverEmailMsgs = Object.keys(errors)
      .filter(k => /^crews\[\d+\]\.email$/.test(k))
      .map((k) => {
        const idx = parseInt(k.match(/\d+/)[0], 10);
        const crew = crews[idx];
        return `${crew ? crew.name : `Crew ${idx + 1}`}: ${errors[k].join(', ')}`;
      });
    return (<ThemeProvider theme={theme}>
      {!isEdit || permissionByWorkroomId(role, workrooms, employee) ? (<Card>
        {this.renderTitle(installer, isEdit)}
        {this.renderTabContent(isEdit, {
          invalidInstallerFieldsMsgs,
          invalidCrewFieldsMsgs,
          invalidHelperFieldsMsgs,
        })}
        <FlexBox justifyContent="flex-end" flexWrap="wrap">
          <CardActions className={styles.actionsWrapper}>
            <ColoredButton
              className={cancelClass}
              responsive
              color="gray"
              label="Cancel"
              onClick={this.handleCancel}
            />
            <ColoredButton
              className={styles.actionButton}
              responsive
              color="green"
              label={isEdit ? 'Save Changes' : 'Add installer'}
              onClick={this.handleSave}
              disabled={fieldsMsgs.length}
            />
          </CardActions>
        </FlexBox>
        {fieldsMsgs.length > 0 && <FlexBox justifyContent="flex-end" flexWrap="wrap">
          <div className={styles.invalidFieldsMsg}>
            {uniq(fieldsMsgs).join(', ')}</div>
        </FlexBox>}
        {serverEmailMsgs.length > 0 && <FlexBox justifyContent="flex-end" flexWrap="wrap">
          <div className={styles.invalidFieldsMsg}>
            {serverEmailMsgs.join(', ')}</div>
        </FlexBox>}
        <ConflictingAppointmentModal
          active={showApptWarningDialog}
          appts={this.crewsAppts()}
          onClose={() => this.handleApptWarningModal(false)}
        />
      </Card>) : <Unauthorized restrictedItem="installer" />}
    </ThemeProvider>);
  }
}

InstallerForm.propTypes = {
  activeTab: PropTypes.number,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onTabChange: PropTypes.func,
  errors: PropTypes.object,
  installer: PropTypes.object,
  helpers: PropTypes.array,
  crews: PropTypes.array,
  workrooms: PropTypes.arrayOf(WorkroomType),
  allWorkrooms: PropTypes.array,
  uploadError: PropTypes.object,
  setErrorMessage: PropTypes.func,
  showUploadDialog: PropTypes.bool,
  fileLoading: PropTypes.bool,
  payRateUploadsData: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    active: PropTypes.bool,
    uploadName: PropTypes.node,
    uploadedBy: PropTypes.string,
    uploadedOn: PropTypes.string,
    statusText: PropTypes.string,
  })),
  setUploadErrorMessage: PropTypes.func,
  onPayRateUpload: PropTypes.func,
  onUploadDialogToggle: PropTypes.func,
  role: PropTypes.string.isRequired,
  user: RoleType,
  employee: PropTypes.object,
  requestEmployee: PropTypes.func,
  updateCrewChanges: PropTypes.func,
  installerFormType: PropTypes.string,
  handleCrewPayId: PropTypes.func,
  crewPayId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default connect(state => ({
  employee: getEmployeeById(state)[state.user.employable_id],
}), { requestEmployee })(InstallerForm);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/installers/components/installerForm.jsx